import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Alert, Card, Button, Form, Modal } from 'react-bootstrap';
import TopNav from '../Components/TopNav';
import { Link } from 'react-router-dom';
import { PageFooter } from './HomePage';

const AccountSettingsPage = () => {
  const { user, updateEmail, updatePassword, updateProfile, deleteAccount } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form states
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deleteConfirmPassword, setDeleteConfirmPassword] = useState('');

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setUsername(user.username || '');
    }
  }, [user]);

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      await updateEmail(email);
      setSuccess('Email updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      await updateProfile({ username });
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      return setError('Passwords do not match');
    }
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      await updatePassword(currentPassword, newPassword);
      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setError('');
      setLoading(true);
      await deleteAccount(deleteConfirmPassword);
      // Redirect will be handled by the auth context
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete account');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="dashboard-container acct-settings">
      <TopNav dark={true} transparent={false} addClass="dashboard-navbar"/>
      <div className="container py-5 main-content">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2>Account Settings</h2>
              <Button as={Link} to="/dashboard" variant="outline-secondary">
                Back to Dashboard
              </Button>
            </div>
            
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="mb-4">
              <Card.Body>
                <h4 className="mb-3">Email Address</h4>
                <Form onSubmit={handleEmailUpdate}>
                  <Form.Group>
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled
                    />
                  </Form.Group>
                  <Button type="submit" disabled={loading} className="d-none">
                    Update Email
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            <Card className="mb-4 d-none">
              <Card.Body>
                <h4 className="mb-3">Profile Information</h4>
                <Form onSubmit={handleProfileUpdate}>
                  <Form.Group>
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Button type="submit" disabled={loading}>
                    Update Profile
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Body>
                <h4 className="mb-3">Change Password</h4>
                <Form onSubmit={handlePasswordUpdate}>
                  <Form.Group className="mb-3">
                    <Form.Label>Current Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Confirm New Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Button type="submit" disabled={loading}>
                    Update Password
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            <Card className="border-danger">
              <Card.Body>
                <h4 className="mb-3 text-danger">Delete Account</h4>
                <p>This action cannot be undone. All your data will be permanently deleted.</p>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                  Delete Account
                </Button>
              </Card.Body>
            </Card>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Delete Account</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Please enter your password to confirm account deletion:</p>
                <Form.Control
                  type="password"
                  value={deleteConfirmPassword}
                  onChange={(e) => setDeleteConfirmPassword(e.target.value)}
                  placeholder="Enter your password"
                />
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleDeleteAccount} disabled={loading}>
                  Delete Account
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </div>
      </div>
      <PageFooter />
    </div>
  );
};

export default AccountSettingsPage; 